import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Target, Users, Clock, Star, CheckCircle, Sparkles, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import { programs, testimonials, whyChooseUs, registrationFee, earlyBirdDiscount } from '../mock';

const HomePage = () => {
  const iconMap = {
    Brain,
    Target,
    Users,
    Clock
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden bg-gradient-to-br from-[#5A9C9B]/10 via-[#f2f5fa] to-[#f26d0f]/10">
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#5A9C9B]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#f26d0f]/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fadeInUp text-center lg:text-left">
              {/* Badge */}
              <Badge className="inline-flex items-center bg-[#f26d0f] hover:bg-[#d35c07] text-white px-6 py-2 text-sm font-medium rounded-full">
                <Sparkles className="h-4 w-4 mr-2" />
                Diskon 20% untuk 30 Pendaftar Pertama!
              </Badge>

              {/* Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                BELAJAR JADI SERU<br />
                <span className="text-[#5A9C9B]">PRESTASI SEMAKIN MAJU!</span>
              </h1>

              {/* Subtitle */}
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Metode Cerdas, Hasil Tuntas<br />
                Siap Hadapi Ujian Bersama BIN Bimbel
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center pt-4">
                <Link to="/pendaftaran" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-[#5A9C9B] hover:bg-[#4a8584] text-white font-semibold px-8 py-6 text-lg rounded-full transition-all hover:scale-105 shadow-lg">
                    Daftar Sekarang
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/program" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto border-2 border-[#5A9C9B] text-[#5A9C9B] hover:bg-[#5A9C9B] hover:text-white font-semibold px-8 py-6 text-lg rounded-full transition-all">
                    Lihat Program
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 max-w-xl mx-auto lg:mx-0 pt-8 border-t border-gray-200/60">
                <div className="text-center lg:text-left">
                  <p className="text-3xl md:text-4xl font-bold text-[#5A9C9B]">10+</p>
                  <p className="text-sm text-gray-600">Tahun Pengalaman</p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-3xl md:text-4xl font-bold text-[#f26d0f]">500+</p>
                  <p className="text-sm text-gray-600">Alumni Sukses</p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-3xl md:text-4xl font-bold text-[#5A9C9B]">95%</p>
                  <p className="text-sm text-gray-600">Tingkat Kepuasan</p>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fadeInUp mt-6 lg:mt-0" style={{ animationDelay: '0.2s' }}>
              <div className="relative z-10 w-full max-w-sm sm:max-w-lg mx-auto lg:max-w-none">
                {/* Decorative blob behind images */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#5A9C9B] to-[#f26d0f] rounded-[40px] rotate-3 opacity-20 scale-105 blur-lg"></div>

                {/* Two images side by side */}
                <div className="relative flex gap-3 items-end">

                  {/* Image 1: Poster prestasi (img8) — kiri, sedikit lebih pendek */}
                  <div className="flex-1 min-w-0">
                    <div className="relative rounded-[20px] overflow-hidden border-4 border-white shadow-xl bg-white aspect-[3/4] group">
                      <img
                        src="/gallery/img8.jpeg"
                        alt="Prestasi Siswa BIN Bimbel"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                      />
                      <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_40px_10px_#ffffff]"></div>
                    </div>
                  </div>

                  {/* Image 2: Foto pelajar (hero-student) — kanan, lebih tinggi */}
                  <div className="flex-1 min-w-0">
                    <div className="relative rounded-[20px] overflow-hidden border-4 border-white shadow-2xl bg-white aspect-[3/5] group">
                      <img
                        src="/gallery/hero-student.jpeg"
                        alt="Pelajar BIN Bimbel"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                      />
                      <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_60px_15px_#ffffff]"></div>
                      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/40 via-transparent to-transparent"></div>

                      {/* Floating badge over image */}
                      <div className="absolute bottom-3 left-2 right-2 bg-white px-2.5 py-2 rounded-xl shadow-xl flex items-center space-x-2 animate-bounce z-20">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                          <Trophy className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="flex flex-col overflow-hidden">
                          <p className="text-gray-900 font-bold text-xs leading-tight truncate">Wisnu Radithya</p>
                          <p className="text-[10px] text-[#5A9C9B] font-semibold leading-tight mt-0.5">SNBP 2026</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-tight truncate">Tek. Rekayasa Komputer IPB</p>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              KENAPA PILIH KAMI?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              BIN Bimbel menawarkan pendekatan pembelajaran yang berbeda dan terbukti efektif
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card
                  key={item.id}
                  className="border-2 border-gray-100 hover:border-[#5A9C9B] transition-all hover:shadow-xl group cursor-pointer animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 text-center space-y-4">
                    <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#5A9C9B] to-[#4a8584] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#f2f5fa] to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              PILIH PROGRAM & HARGA SUKSESMU!
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Program belajar yang dirancang sesuai dengan kebutuhan dan target Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.slice(0, 3).map((program, index) => (
              <Card
                key={program.id}
                className="border-2 hover:border-[#f26d0f] transition-all hover:shadow-2xl group overflow-hidden animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="h-2 bg-gradient-to-r from-[#5A9C9B] to-[#f26d0f]"></div>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <Badge className="mb-3 bg-[#5A9C9B]/10 text-[#5A9C9B] hover:bg-[#5A9C9B]/20">
                      {program.level}
                    </Badge>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{program.title}</h3>
                    <p className="text-sm text-gray-600">{program.description}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-[#f26d0f]">{program.price}</span>
                      <span className="text-gray-600 ml-1">{program.period}</span>
                    </div>
                    <p className="text-sm text-gray-600">{program.frequency}</p>
                    <p className="text-sm font-medium text-[#5A9C9B]">{program.maxStudents}</p>
                  </div>

                  <ul className="space-y-2">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-[#5A9C9B] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/pendaftaran" className="block">
                    <Button className="w-full bg-[#f26d0f] hover:bg-[#d35c07] text-white font-semibold rounded-full group-hover:scale-105 transition-all">
                      Daftar Program Ini
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/program">
              <Button variant="outline" className="border-2 border-[#5A9C9B] text-[#5A9C9B] hover:bg-[#5A9C9B] hover:text-white font-semibold px-8 py-3 rounded-full">
                Lihat Semua Program
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Promo Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#f26d0f] to-[#d35c07]">
        <div className="container mx-auto">
          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="flex-1 space-y-4">
                  <Badge className="bg-red-500 text-white hover:bg-red-600">
                    <Sparkles className="h-4 w-4 mr-1" />
                    PROMO TERBATAS
                  </Badge>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    Diskon Spesial 'Early Bird' 20%!
                  </h2>
                  <p className="text-lg text-gray-700">
                    Hanya untuk 30 pendaftar pertama bulan ini!<br />
                    <span className="font-semibold text-[#5A9C9B]">+ Bonus Kakak Adik & Referral Teman!</span>
                  </p>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Trophy className="h-5 w-5 text-[#f26d0f]" />
                    <span>Biaya Pendaftaran: Rp {registrationFee.toLocaleString('id-ID')}</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Link to="/pendaftaran">
                    <Button className="bg-[#5A9C9B] hover:bg-[#4a8584] text-white font-bold px-10 py-6 text-lg rounded-full shadow-lg hover:scale-105 transition-all">
                      Daftar & Dapatkan Diskon!
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 bg-white overflow-hidden">
        <div className="container mx-auto">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 uppercase">
              Apa Pendapat Mereka
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Simak Pendapat Siswa Mengenai Program Belajar di BIN BIMBEL
            </p>
          </div>

          <div className="max-w-6xl mx-auto px-4 sm:px-12">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {[
                  {
                    src: "/gallery/carousel-1.jpg",
                    name: "Firyal Hilma Fauzia",
                    jalur: "SNBP 2026",
                    jurusan: "Akuntansi Unsil",
                    quote: "Awalnya aku ragu mau les di mana, tapi setelah coba BINBIMBEL langsung kerasa bedanya. Kakak tutornya sabar banget jelasin materi yang aku belum ngerti. Alhamdulillah nilai tryout aku naik terus sampai akhirnya tembus PTN impian!"
                  },
                  {
                    src: "/gallery/carousel-2.jpg",
                    name: "Rifa Nasti",
                    jalur: "SBMPTN 2026",
                    jurusan: "Pendidikan Matematika UPI",
                    quote: "Jujur aku tipe yang susah fokus kalau belajar sendiri. Di BINBIMBEL beda, suasananya bikin semangat dan materinya dikasih dengan cara yang gampang dipahami. Aku rekomendasiin banget buat yang lagi persiapan SBMPTN!"
                  },
                  {
                    src: "/gallery/carousel-3.jpg",
                    name: "Yusriana NusaPutri",
                    jalur: "SBMPTN 2026",
                    jurusan: "Kimia UIN Malang",
                    quote: "BIN BIMBEL beneran bantu aku dari nol. Pas masuk pertama kali nilai simulasiku jelek banget, tapi kakak pengajarnya nggak pernah nyerah buat bantu aku. Sekarang udah diterima di jurusan yang aku impiin sejak dulu."
                  },
                  {
                    src: "/gallery/carousel-4.jpg",
                    name: "Rena Tajia Rahayu",
                    jalur: "SNBP 2026",
                    jurusan: "PGPAUD UPI",
                    quote: "Yang bikin aku suka belajar di BINBIMBEL itu kakak-kakaknya asik, nggak kaku, jadi nanya pun nggak malu. Materinya juga lengkap dan selalu update. Berkat BINBIMBEL aku makin pede ngadepin Ujian!"
                  },
                  {
                    src: "/gallery/carousel-5.jpg",
                    name: "Rifda Kamilah",
                    jalur: "Mandiri 2026",
                    jurusan: "Administrasi Publik Unsoed",
                    quote: "Belajar di BINBIMBEL itu nggak kerasa kayak les biasa, lebih kayak belajar bareng temen tapi ilmunya masuk. Metodenya beda dari tempat les lain yang pernah aku coba. Hasilnya? Alhamdulillah sesuai harapan"
                  }
                ].map((item, index) => (
                  <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <div className="p-1 h-full">
                      <Card className="overflow-hidden border-0 shadow-lg rounded-2xl group cursor-pointer h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
                        
                        {/* Bagian Atas: Foto & Lencana */}
                        <div className="relative h-[320px] shrink-0 overflow-hidden">
                          <img 
                            src={item.src} 
                            alt={`Galeri ${item.name}`} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 absolute inset-0 z-0"
                          />
                          
                          {/* Overlay Gradasi */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent z-10 pointer-events-none opacity-80 group-hover:opacity-100 transition-opacity duration-300"></div>

                          {/* Lencana (Badge) Identitas */}
                          <div className="absolute bottom-4 left-4 right-4 z-20">
                            <div className="bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-xl flex items-center space-x-3 transform transition-all duration-300 group-hover:-translate-y-1">
                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                                <Trophy className="h-5 w-5 text-green-600" />
                              </div>
                              <div className="flex flex-col overflow-hidden">
                                <p className="text-gray-900 font-bold text-sm leading-tight truncate">{item.name}</p>
                                <p className="text-xs text-[#5A9C9B] font-semibold leading-tight mt-0.5">{item.jalur}</p>
                                <p className="text-[11px] text-gray-500 mt-1 leading-tight truncate" title={item.jurusan}>{item.jurusan}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bagian Bawah: Kotak Testimoni */}
                        <div className="p-6 bg-white flex-1 flex flex-col border-t-2 border-gray-50">
                          <div className="flex space-x-1 mb-3">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="h-4 w-4 fill-[#f26d0f] text-[#f26d0f]" />
                            ))}
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed italic">
                            "{item.quote}"
                          </p>
                        </div>
                      </Card>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex bg-white text-[#5A9C9B] border-[#5A9C9B] hover:bg-[#5A9C9B] hover:text-white" />
              <CarouselNext className="hidden md:flex bg-white text-[#5A9C9B] border-[#5A9C9B] hover:bg-[#5A9C9B] hover:text-white" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#5A9C9B] to-[#4a8584] text-white">
        <div className="container mx-auto text-center space-y-8 animate-fadeInUp">
          <h2 className="text-3xl md:text-5xl font-bold">
            JANGAN TUNDA PRESTASIMU
          </h2>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto">
            Bergabunglah dengan ratusan siswa yang telah merasakan metode belajar efektif kami
          </p>
          <Link to="/pendaftaran">
            <Button className="bg-[#f26d0f] hover:bg-[#d35c07] text-white font-bold px-12 py-6 text-lg rounded-full shadow-2xl hover:scale-105 transition-all">
              Daftar Sekarang!
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
